import { getBootstrapIconUrl, getBootstrapIconUrlByKey, BootstrapIcon } from '../../Global/icons'
import {
  tryLoadDependencies,
  tryLoadLightDOMStyles,
  WebAwesomeComponent,
} from '../../Global/loadDependency'
import {
  NavPortalMenuItemAttr,
  NavPortalMenuItemClass,
  NavPortalMenuItemDefaults,
  NavPortalMenuItemTarget,
  NavPortalMenuItemValues,
  NavPortalMenuItemSelector,
} from './types/NavPortalMenuItem.type'
import { getNavPortalMenuItemStyles } from './NavPortalMenuItem.styles'

const Home = getBootstrapIconUrl(BootstrapIcon.Home)

class NavPortalMenuItem extends HTMLElement {
  private linkElement: HTMLAnchorElement | null = null
  private iconElement: HTMLElement | null = null
  private labelElement: HTMLSpanElement | null = null
  private initialized = false

  constructor() {
    super()
    tryLoadLightDOMStyles('NavPortalMenuItem.css')
    tryLoadDependencies(document, [WebAwesomeComponent.Details, WebAwesomeComponent.Icon])
  }

  connectedCallback() {
    document.AscendAwesome?.LogManager?.logDebug('NavPortalMenuItem connectedCallback')

    // Only initialize DOM once
    if (!this.initialized) {
      const expandable = this.hasAttribute(NavPortalMenuItemAttr.EXPANDABLE)

      const shadow = this.attachShadow({ mode: 'open' })
      shadow.appendChild(getNavPortalMenuItemStyles().content.cloneNode(true))

      if (expandable) {
        const t = document.createElement('template')
        t.innerHTML = `
          <wa-details appearance="plain" exportparts="base,header,summary,content,icon" role="group" class="${NavPortalMenuItemClass.MENU_ITEM}" href="${NavPortalMenuItemDefaults.HREF}"> 
            <div slot="summary" >
              <wa-icon src="${Home}" label="icon"></wa-icon>  
              <span class="${NavPortalMenuItemClass.LABEL}"><slot></slot></span>
            </div>
            <slot></slot>
          </wa-details>
        `
        shadow.appendChild(t.content.cloneNode(true))
      } else {
        const t = document.createElement('template')
        t.innerHTML = `
          <a class="${NavPortalMenuItemClass.MENU_ITEM}" href="${NavPortalMenuItemDefaults.HREF}">
            <wa-icon src="${Home}" label="icon" ${NavPortalMenuItemAttr.ARIA_HIDDEN}="${NavPortalMenuItemValues.ARIA_HIDDEN_TRUE}"></wa-icon>
            <span class="${NavPortalMenuItemClass.LABEL}"></span>
          </a>
        `
        shadow.appendChild(t.content.cloneNode(true))
      }

      // Query elements from shadow DOM
      this.linkElement = shadow.querySelector(NavPortalMenuItemSelector.ANCHOR)
      this.iconElement = shadow.querySelector(NavPortalMenuItemSelector.ICON)
      this.labelElement = shadow.querySelector(NavPortalMenuItemSelector.LABEL)
      this.initialized = true
    }

    this.updateLink()
    this.updateTarget()
    this.updateIcon()
    this.updateLabel()
    this.updateAriaLabel()
    this.updateActiveState()
  }

  static get observedAttributes() {
    return [
      NavPortalMenuItemAttr.HREF,
      NavPortalMenuItemAttr.LABEL,
      NavPortalMenuItemAttr.ICON,
      NavPortalMenuItemAttr.ACTIVE,
      NavPortalMenuItemAttr.TARGET,
      NavPortalMenuItemAttr.ARIA_LABEL,
      NavPortalMenuItemAttr.EXPANDABLE,
    ]
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
    if (oldValue === newValue) return

    switch (name) {
      case NavPortalMenuItemAttr.HREF:
        this.updateLink()
        break
      case NavPortalMenuItemAttr.TARGET:
        this.updateTarget()
        break
      case NavPortalMenuItemAttr.LABEL:
        this.updateLabel()
        break
      case NavPortalMenuItemAttr.ICON:
        this.updateIcon()
        break
      case NavPortalMenuItemAttr.ACTIVE:
        this.updateActiveState()
        break
      case NavPortalMenuItemAttr.ARIA_LABEL:
        this.updateAriaLabel()
        break
    }
  }

  private updateLink() {
    if (!this.linkElement) return
    const href = this.getAttribute(NavPortalMenuItemAttr.HREF) || NavPortalMenuItemDefaults.HREF
    this.linkElement.href = href
  }

  private updateTarget() {
    if (!this.linkElement) return
    const target = this.getAttribute(NavPortalMenuItemAttr.TARGET)
    if (target) {
      this.linkElement.setAttribute(NavPortalMenuItemAttr.TARGET, target)
      // Add rel="noopener noreferrer" for security when opening in new tab
      if (target === NavPortalMenuItemTarget.BLANK) {
        this.linkElement.setAttribute(
          NavPortalMenuItemAttr.REL,
          NavPortalMenuItemValues.REL_NOOPENER,
        )
      }
    } else {
      this.linkElement.removeAttribute(NavPortalMenuItemAttr.TARGET)
      this.linkElement.removeAttribute(NavPortalMenuItemAttr.REL)
    }
  }

  private updateLabel() {
    if (!this.labelElement) return
    const label = this.getAttribute(NavPortalMenuItemAttr.LABEL) || NavPortalMenuItemDefaults.LABEL
    this.labelElement.textContent = label
  }

  private updateIcon() {
    if (!this.iconElement) return
    const iconName = this.getAttribute(NavPortalMenuItemAttr.ICON) || NavPortalMenuItemDefaults.ICON
    try {
      this.iconElement.setAttribute('src', getBootstrapIconUrlByKey(iconName))
    } catch {
      // Fall back to a known valid icon if the requested icon is not approved.
      this.iconElement.setAttribute('src', Home)
    }
  }

  private updateActiveState() {
    if (!this.linkElement) return
    const isActive = this.hasAttribute(NavPortalMenuItemAttr.ACTIVE)
    this.linkElement.classList.toggle(NavPortalMenuItemClass.ACTIVE, isActive)
    // Use 'page' for current page in navigation, 'false' otherwise
    this.linkElement.setAttribute(
      NavPortalMenuItemAttr.ARIA_CURRENT,
      isActive
        ? NavPortalMenuItemValues.ARIA_CURRENT_PAGE
        : NavPortalMenuItemValues.ARIA_CURRENT_FALSE,
    )
  }

  private updateAriaLabel() {
    if (!this.linkElement) return
    const ariaLabel = this.getAttribute(NavPortalMenuItemAttr.ARIA_LABEL)
    if (ariaLabel) {
      this.linkElement.setAttribute(NavPortalMenuItemAttr.ARIA_LABEL, ariaLabel)
    } else {
      this.linkElement.removeAttribute(NavPortalMenuItemAttr.ARIA_LABEL)
    }
  }
}

if (!customElements.get('ts-navportal-menu-item')) {
  customElements.define('ts-navportal-menu-item', NavPortalMenuItem)
}
export { NavPortalMenuItem }
